import React, { useCallback } from 'react';
import { FooterActions } from './footer-actions';
import { ModalState } from 'tradera/components/bank-id/state/bank-id/modal-state';
import { close } from 'tradera/components/bank-id/state/bank-id/reducer';
import { BankIdModalBody } from './bank-id-body';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { webApiClient } from 'tradera/utils/http';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import type {
    BankIdAuthenticationCancelApiRequest,
    BankIdAuthenticationCancelApiResponse,
    BankIdSignatoryAuthenticationCancelApiResponse,
    BankIdSignatoryAuthenticationCancelApiRequest
} from 'tradera/state/services/types/webapi-auth-generated';
import { BankIdLogoColor } from 'tradera/components/bank-id/bank-id-logo-color';
import styles from './bank-id-modal.module.scss';
import { classNames } from 'tradera/utils/class-names';

const CLOSED_STATES = [ModalState.CLOSED, ModalState.COMPLETED];

type BankIdModalProps = {
    onClose?: () => void;
    onTop?: boolean;
};

export const BankIdModal = ({ onTop, onClose }: BankIdModalProps) => {
    const dispatch = useAppDispatch();

    const isMobile = useIsMobile();
    const [mobileFlow, setMobileFlow] = React.useState(isMobile);
    const {
        errorCode,
        autoStartToken,
        qrData,
        modalState,
        bankIdOrderRef,
        contactSsn,
        memberId,
        purpose,
        organizationNumber
    } = useAppSelector((state) => state.bankId);

    const closeModal = useCallback(async () => {
        dispatch(close());
        onClose?.();
        setMobileFlow(isMobile);

        if (modalState === ModalState.IN_PROGRESS) {
            if (!bankIdOrderRef) return; // bankIdOrderRef is needed to properly cancel
            if (contactSsn) {
                webApiClient.post<
                    BankIdSignatoryAuthenticationCancelApiResponse,
                    BankIdSignatoryAuthenticationCancelApiRequest
                >('/auth/bankid/cancel-signatory', {
                    bankIdOrderRef,
                    contactSsn,
                    organizationNumber
                });
            } else {
                webApiClient.post<
                    BankIdAuthenticationCancelApiResponse,
                    BankIdAuthenticationCancelApiRequest
                >('/auth/bankid/cancel', {
                    bankIdOrderRef,
                    memberId
                });
            }
        }
    }, [
        dispatch,
        onClose,
        isMobile,
        modalState,
        contactSsn,
        organizationNumber,
        bankIdOrderRef,
        memberId
    ]);

    if (CLOSED_STATES.includes(modalState)) {
        return null;
    }

    return (
        <ScrollableModal
            show
            onTop={onTop}
            maxWidth="40rem"
            maxHeight={isMobile ? '97vh' : '52rem'}
            mobileBottomSheet
            onClose={closeModal}>
            <div
                className={classNames(
                    'd-flex flex-column px-1 px-md-3 pt-1 pt-md-2',
                    styles.minHeight
                )}>
                <div className="d-flex flex-column justify-content-center align-items-center text-center flex-grow-1">
                    <BankIdLogoColor
                        className={classNames(
                            styles.bankIdLogo,
                            modalState === ModalState.IN_PROGRESS &&
                                styles.inProgress
                        )}
                    />
                    <BankIdModalBody
                        modalState={modalState}
                        errorCode={errorCode}
                        showQrCode={!mobileFlow}
                        qrData={!mobileFlow ? qrData : undefined}
                    />
                </div>
                <div>
                    <FooterActions
                        setMobileFlow={setMobileFlow}
                        mobileFlow={mobileFlow}
                        onClose={closeModal}
                        modalState={modalState}
                        autoStartToken={autoStartToken}
                        memberId={memberId || undefined}
                        purpose={purpose || 'Unknown'}
                    />
                </div>
            </div>
        </ScrollableModal>
    );
};
